import React from "react"
import { graphql } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import { RebassImage, Box, Flex, Text } from "@singita/components"
import { mapperWithFunction, processFluid } from "../common/utils"
import ResponsiveImage from "./ResponsiveImage"

const IMAGE_MAP = {
  isBackgroundImage: value => {
    return !!(value.focalPoint && value.focalPoint.focalPoint)
  },
  Component: value => {
    return value.focalPoint && value.focalPoint.focalPoint
      ? GatsbyImage
      : GatsbyImage
  },
  style: value => {
    const { focalPoint: fp } = value
    const { focalPoint } = fp || {}
    const baseStyle = { height: "100%", width: "100%", ...(value.style || {}) }
    if (focalPoint) {
      return {
        ...baseStyle,
        ...(focalPoint.xp && focalPoint.yp
          ? {
              backgroundPosition: `${focalPoint.xp}% ${focalPoint.yp}%`,
            }
          : {}),
        position: "absolute",
      }
    }
    return baseStyle
  },
  alt: ({ altText, caption }) => {
    return altText || caption || ""
  },
  image: value => {
    return value?.gatsbyImageData
      ? value
      : value.image?.gatsbyImageData
      ? value.image
      : null
  },
  fluid: value => {
    const fluid = value.image ? value.image.fluid : value.fluid
    const processedFluid = processFluid(fluid)
    return processedFluid
    // return value.image ? value.image.fluid : value.fluid
  },
  imgStyle: value => {
    const baseStyle = {
      objectFit: "cover",
      ...value.imgStyle,
    }
    if (value.focalPoint?.focalPoint?.xp && value.focalPoint?.focalPoint?.yp) {
      baseStyle.objectPosition = `${value.focalPoint.focalPoint.xp}% ${value.focalPoint.focalPoint.yp}%`
    }
    return baseStyle
  },
  containerSx: value => {
    return {
      ...value.containerSx,
      ...(value.focalPoint && value.focalPoint.focalPoint
        ? {
            paddingBottom: `${
              100 /
              (value.image.gatsbyImageData.aspectRatio ||
                value.image.gatsbyImageData.width /
                  value.image.gatsbyImageData.height)
            }%`,
            position: "relative",
          }
        : {}),
    }
  },
}

const ImageWithCaption = ({ children, style, caption, onClick }) => {
  return (
    <Flex
      height="100%"
      width="100%"
      flexDirection="column"
      sx={style}
      onClick={onClick}
    >
      {children}
      {caption && (
        <Box mt={[1]} textAlign="center">
          <Text size="tiny">{caption}</Text>
        </Box>
      )}
    </Flex>
  )
}

const ImageComponent = ({ imageProps }) => {
  const {
    isBackgroundImage,
    image,
    fluid,
    imgStyle,
    alt,
    altText,
    style,
    containerSx,
    ...rest
  } = imageProps
  if (!image && !fluid) return null
  if (isBackgroundImage) {
    return (
      <GatsbyImage
        {...imgStyle}
        style={style}
        alt={alt || altText || ""}
        image={image?.gatsbyImageData || image}
        {...rest}
      />
    )
  }
  if (fluid) {
    return (
      <ResponsiveImage
        fluid={fluid}
        {...imgStyle}
        style={style}
        alt={alt || altText || "  "}
        {...rest}
      />
    )
  }

  return (
    <GatsbyImage
      {...imgStyle}
      alt={alt || altText || ""}
      style={style}
      image={image?.gatsbyImageData || image}
      {...rest}
    />
  )
}

const ImageWrapper = props => {
  const { url, style = {}, imgStyle, caption, image, onClick, ...rest } = props
  const imageUrl = url || image?.url
  if (imageUrl) {
    return (
      <ImageWithCaption
        style={{ ...style, ...props.containerSx }}
        caption={caption}
        onClick={onClick}
      >
        <RebassImage
          src={imageUrl}
          sx={{ ...imgStyle, height: "100%", width: "100%" }}
        />
      </ImageWithCaption>
    )
  }
  const { Component, containerSx, ...imageProps } = mapperWithFunction(
    props,
    IMAGE_MAP
  )
  return imageProps.image || imageProps.fluid ? (
    <ImageWithCaption style={containerSx} caption={caption} onClick={onClick}>
      <ImageComponent imageProps={{ ...imageProps, ...rest }} />
    </ImageWithCaption>
  ) : null
}

export default ImageWrapper

export const ImageFields = graphql`
  fragment ImageFields on ContentfulImage {
    id
    contentful_id
    name
    caption
    altText
    focalPoint {
      focalPoint {
        x
        y
        xp
        yp
      }
    }
  }
`

export const ArticleImageFields = graphql`
  fragment ArticleImageFields on ContentfulImage {
    id
    contentful_id
    name
    caption
    altText
    image {
      # fluid(quality: 95, maxWidth: 1000) {
      #   ...GatsbyContentfulFluid_withWebp_noBase64
      # }
      gatsbyImageData(
        placeholder: NONE
        layout: CONSTRAINED
        quality: 95
        width: 1000
        formats: [AUTO, WEBP]
      )
    }
    focalPoint {
      focalPoint {
        x
        y
        xp
        yp
      }
    }
  }
`

export const GalleryLayoutFields = graphql`
  fragment GalleryLayoutFields on ContentfulImage {
    id
    contentful_id
    name
    caption
    altText
    image {
      # fluid(quality: 95, maxWidth: 1200) {
      #   ...GatsbyContentfulFluid_withWebp_noBase64
      # }
      gatsbyImageData(
        placeholder: NONE
        layout: CONSTRAINED
        quality: 95
        width: 1200
        formats: [AUTO, WEBP]
      )
    }
  }
`
